<template>
  <v-dialog
    v-model="dialog"
    :persistent="!selectedLocale"
    max-width="650"
    class="language-dialog app-sm-and-down"
    @click:outside="handleOutsideClick"
  >
    <v-card class="app-modal-card pa-0 overflow-hidden rounded-xl">
      <div class="dialog-grid">
        <!-- Left side - Founder message -->
        <div class="founder-section">
          <div class="founder-content">
            <div class="founder-header">
              <div class="founder-avatar">
                <v-avatar size="64" class="border-primary">
                  <v-img
                    class="founder-avatar-img"
                    :src="getImagePath('nanna-founder-knittable.jpg')"
                    :alt="$t('language_selection.founder_title')"
                    :cover="true"
                  ></v-img>
                </v-avatar>
              </div>
              <div class="founder-info">
                <h3 class="founder-title mb-0">{{ $t("language_selection.founder_title") }}</h3>
                <p class="founder-role text-medium-emphasis">
                  {{ $t("language_selection.founder_role") }}
                </p>
              </div>
            </div>

            <div class="founder-message mt-0">
              <h2 class="message-title">{{ $t("language_selection.title") }}</h2>
              <p class="message-text text-medium-emphasis mt-0">
                {{ $t("language_selection.message") }}
              </p>
            </div>
          </div>
        </div>

        <!-- Right side - Language selection -->
        <div class="language-section">
          <div class="language-content">
            <div class="language-options-container">
              <div class="language-options">
                <v-card
                  v-for="option in languageItems"
                  :key="option.value"
                  :class="{
                    'selected-language': selectedLocale && selectedLocale.value === option.value,
                  }"
                  class="language-option"
                  @click="selectLanguage(option)"
                  flat
                  variant="outlined"
                >
                  <v-card-text class="d-flex align-center justify-space-between pa-3">
                    <div class="d-flex align-center">
                      <span class="flag flag-icon mr-3">
                        <span v-if="option.value === 'en'" class="flag">🇬🇧</span>
                        <span v-if="option.value === 'is'" class="flag">🇮🇸</span>
                        <span v-if="option.value === 'fi'" class="flag">🇫🇮</span>
                        <span v-if="option.value === 'de'" class="flag">🇩🇪</span>
                        <span v-if="option.value === 'da'" class="flag">🇩🇰</span>
                      </span>
                      <span class="font-weight-medium">{{ getNativeName(option.value) }}</span>
                    </div>
                    <v-icon
                      v-if="selectedLocale && selectedLocale.value === option.value"
                      color="primary"
                      size="small"
                    >
                      mdi-check
                    </v-icon>
                  </v-card-text>
                </v-card>
              </div>
            </div>

            <div class="continue-button-container app-sm-and-down">
              <main-button
                color="purple"
                block
                @click="saveSelection"
                :disabled="!selectedLocale"
                :compact="true"
              >
                {{ $t("language_selection.continue") }}
              </main-button>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted, defineExpose, defineProps, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useLangStore } from "../stores/lang";
import { composeRoute } from "../lib/navigationUtils.js";
import { getImagePath } from "@/lib/imageUtils";
import { useI18n } from "vue-i18n";
import MainButton from "./general/MainButton.vue";
import avo from "./../userTracking/Avo";

const { t } = useI18n();

// Define props
const props = defineProps({
  autoOpenIfNoLanguage: {
    type: Boolean,
    default: false,
  },
});

const STORAGE_KEY = "selectedLanguage";

const router = useRouter();
const route = useRoute();
const langStore = useLangStore();
const dialog = ref(false);

// Define the custom order for languages
const languageOrder = ["is", "en", "fi", "de", "da"];

const languageOptions = langStore.getSupportedLocales;
// Initialize selectedLocale as null so nothing is pre-selected
const selectedLocale = ref(null);

const languageItems = computed(() => {
  // Create items from supported locales
  const items = Object.entries(languageOptions).map(([code, label]) => ({
    text: label,
    value: code,
    beta: code === "fi", // Mark Finnish as beta if needed
  }));

  // Sort items according to languageOrder
  return items.sort((a, b) => {
    const aIndex = languageOrder.indexOf(a.value);
    const bIndex = languageOrder.indexOf(b.value);
    return aIndex - bIndex;
  });
});

const getLanguageName = (code) => {
  const names = {
    en: "English",
    is: "Icelandic",
    fi: "Finnish",
    de: "German",
    da: "Danish",
  };
  return names[code] || code.toUpperCase();
};

const getNativeName = (code) => {
  const nativeNames = {
    en: "English",
    is: "Íslenska",
    fi: "Suomi",
    de: "Deutsch",
    da: "Dansk",
  };
  return nativeNames[code] || "";
};

// This function selects a language without immediately saving
const selectLanguage = (option) => {
  selectedLocale.value = option;
  // Update the i18n locale temporarily to show the text in the selected language
  langStore.setLocale(option.value);
};

// Handle clicking outside the dialog
const handleOutsideClick = () => {
  if (selectedLocale.value) {
    saveSelection();
  }
};

// This handles the actual saving and navigation
const saveSelection = () => {
  if (!selectedLocale.value) return;

  const newLocale = selectedLocale.value.value;
  const currentLocale = langStore.getLocale();

  // Save to local storage
  localStorage.setItem(STORAGE_KEY, newLocale);

  // Update the application locale
  langStore.setLocale(newLocale);

  // Navigate to the same route but with the new locale
  if (route && route.name) {
    // Get all params except locale
    const routeParams = { ...route.params };
    delete routeParams.locale; // Remove locale as it will be set by composeRoute

    // Create the route with new locale
    const newRoute = composeRoute(newLocale, route.name);

    // Add all original params to the new route
    newRoute.params = { ...newRoute.params, ...routeParams };

    // Also preserve query and hash if present
    if (Object.keys(route.query).length > 0) {
      newRoute.query = route.query;
    }

    if (route.hash) {
      newRoute.hash = route.hash;
    }

    router.push(newRoute);
  } else {
    // Fallback if route is not available
    router.push({ name: "home", params: { locale: newLocale } });
  }

  // Close the dialog
  dialog.value = false;

  avo.selectLanguage({
    language: newLocale,
  });
};

// Watch for dialog state changes to reset language if dialog is closed without saving
watch(dialog, (newValue) => {
  if (!newValue && selectedLocale.value) {
    // Reset to the current locale if dialog is closed without saving
    const currentLocale = langStore.getLocale();
    langStore.setLocale(currentLocale.value);
  }
});

onMounted(() => {
  // Check if language is already stored in local storage
  const storedLanguage = localStorage.getItem(STORAGE_KEY);

  if (storedLanguage && Object.keys(languageOptions).includes(storedLanguage)) {
    // If stored and valid, set the language without showing the dialog
    const currentLocale = langStore.getLocale();

    // Find the option object that matches the stored language
    const storedOption = languageItems.value.find((item) => item.value === storedLanguage);
    if (storedOption) {
      selectedLocale.value = storedOption;
    }

    if (storedLanguage !== currentLocale.value) {
      // Only update the language but don't redirect - the router guard will handle that
      langStore.setLocale(storedLanguage);
    }
  } else if (props.autoOpenIfNoLanguage) {
    // Only open the dialog automatically if the prop is set and no language is stored
    dialog.value = true;
  }
});

// Expose the dialog ref to parent components
defineExpose({
  dialog,
});
</script>

<style lang="scss" scoped>
@import "../../scss/styles.scss";
@import "../../scss/modal.scss";

.language-dialog {
  .v-overlay__content {
    max-width: 650px;
    width: 100%;
  }
}

.app-modal-card {
  overflow: hidden;
  border-radius: 16px;
}

.dialog-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.founder-section {
  background-color: var(--knittable-lav-light);
  background-opacity: 0.1;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 600px) {
    padding: 16px;
  }

  @media (min-width: 601px) {
    align-items: center;
    .founder-content {
      align-items: center;
      text-align: center;
    }
    .founder-header {
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }
    .founder-avatar {
      .v-avatar {
        width: 160px !important;
        height: 160px !important;
        font-size: 160px !important;
      }
      .founder-avatar-img {
        width: 160px !important;
        height: 160px !important;
      }
    }
  }
}

.founder-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.founder-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.founder-avatar {
  .border-primary {
    border: 2px solid var(--v-primary-base);
  }
}

.founder-avatar-img {
  object-fit: cover;
}

.founder-message {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.founder-title {
  @include paragraphMediumBold;
}

.founder-role {
  @include paragraphSmallRegular;
}

.message-title {
  @include subtitleSmall;
}

.message-text {
  @include paragraphSmallRegular;
  color: var(--knittable-lav-darker);
}

.flag-icon {
  font-size: 24px;
}

.language-section {
  padding: 24px;
  display: flex;
  flex-direction: column;
  height: 100%;

  @media (max-width: 600px) {
    padding: 16px;
  }
}

.language-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
  min-height: 0;
}

.language-options-container {
  flex: 1;
  overflow: hidden;
  min-height: 0;
  height: calc(100vh - 300px);
  max-height: 300px;

  @media (max-width: 600px) {
    height: calc(100vh - 250px);
    max-height: 185px;
  }
}

.language-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
}

.language-option {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  border: 2px solid var(--knittable-lav-light);
  min-height: 45px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }

  span {
    @include paragraphMediumRegular;
  }

  .v-card-text {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    min-height: 45px;
  }
}

.selected-language {
  border: 2px solid var(--knittable-lav-darker);
  background-color: rgba(var(--v-primary-base-rgb), 0.05);
}

.flag {
  font-size: 24px;
}

.continue-button-container {
  display: flex;
  justify-content: center;
  padding-top: 16px;
  margin-top: auto;
}

.app-sm-and-down {
  .v-overlay__content {
    height: 100%;
  }
  .continue-button-container {
    padding-top: 0;
    margin-top: 0;
  }
}

.app-btn {
  height: 48px;

  .button-text {
    @include buttonText;
    text-transform: capitalize;
  }
}

.founder-info {
  h3 {
    @include paragraphMediumBold;
  }

  p {
    @include paragraphSmallRegular;
  }
}
</style>
